import type { JwtPayload, User } from '@supabase/supabase-js';
import type { SessionClaimsUser, SessionClaimsUserMetadata } from '../types/jwt.types';

/**
 * Maps a Supabase Auth User (from `getUser()`) to the sparse session user shape.
 */
export function mapSupabaseUserToSessionClaimsUser(user: User): SessionClaimsUser {
  return {
    id: user.id,
    app_metadata: user.app_metadata,
    user_metadata: mapUserMetadata(user.user_metadata),
    confirmed_at: stringClaim(user.confirmed_at),
    email_confirmed_at: stringClaim(user.email_confirmed_at),
    updated_at: stringClaim(user.updated_at),
  };
}

/**
 * Maps verified JWT claims to the sparse session user shape used by getUserSession/getUserInfo.
 * Mirrors the field selection in getValidatedUser() (jose JWKS path).
 *
 * Only `id` (from `sub`) is guaranteed for session consumers; other fields are included when
 * present on the JWT but may be undefined. Full profile data comes from `fetchFullUser()`.
 */
export function mapClaimsToSugarFreeUser(claims: JwtPayload): SessionClaimsUser {
  const sub = claims.sub?.trim();
  if (!sub) {
    throw new Error('JWT claims missing sub');
  }

  return {
    id: sub,
    app_metadata: claims.app_metadata,
    user_metadata: mapUserMetadata(claims.user_metadata),
    confirmed_at: stringClaim(claims.confirmed_at),
    email_confirmed_at: stringClaim(claims.email_confirmed_at),
    updated_at: stringClaim(claims.updated_at),
  };
}

function mapUserMetadata(value: unknown): SessionClaimsUserMetadata | undefined {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  const meta = value as Record<string, unknown>;
  const avatar_url = stringClaim(meta.avatar_url) ?? stringClaim(meta.picture);
  const email = stringClaim(meta.email);
  const name = stringClaim(meta.name) ?? stringClaim(meta.full_name);

  if (!avatar_url && !email && !name) {
    return undefined;
  }

  return {
    ...(avatar_url ? { avatar_url } : {}),
    ...(email ? { email } : {}),
    ...(name ? { name } : {}),
  };
}

function stringClaim(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}
